import React, { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import * as localService from "../../services/localService";
import Lottie from "lottie-react";
import { FeatureAccess } from "../../utils/featureAccessControl";
import { buildPaymentRedirectDeepLink, isMobileDevice, openDeepLink } from "../../utils/deepLink";
import { usePostAuthFlow } from "../../shared/auth/postAuthFlow";
import apiV2Service from "../../services/apiV2Service";
import logger from "../../utils/logger";


const INVOICE_PAYMENT_REDIRECT_KEY = "invoicePaymentRedirect";

export default function PaymentRedirect() {
  const history = useHistory();
  const location = useLocation();
  const [animationData, setAnimationData] = useState(null);

  const { checkOrganizationSubscriptions, loadSubscriptionFeatureFlags } = usePostAuthFlow();

  const timerRef = useRef(null);

  useEffect(() => {
    // Load animation JSON from public to avoid compile-time import failures
    fetch("/assets/animations/payment-success.json")
      .then(res => (res.ok ? res.json() : null))
      .then(data => setAnimationData(data))
      .catch(() => setAnimationData(null));
  }, []);

  useEffect(() => {
    const processPaymentRedirect = async () => {
      try {
        const params = new URLSearchParams(location.search);
        const reference = params.get("reference") || params.get("references") || "";
        const status = params.get("status") || "";

        if (reference) {
          try {
            localStorage.setItem("lastPaymentReference", reference);
          } catch (_) {}
        }

        if (status === "success" || !status) {
          try {
            const user = localService.getUser();
            if (user && user.organizationId) {
              await new Promise(resolve => setTimeout(resolve, 1000));

              const { activeRecurringSubscription } = await checkOrganizationSubscriptions(user.organizationId);
              await loadSubscriptionFeatureFlags(activeRecurringSubscription);

              const userRes = await apiV2Service.getSelfUser();
              if (userRes?.status === 200 && userRes?.data) {
                localService.saveUser({
                  ...userRes.data,
                  access_token: localService.getAuthToken(),
                  refresh_token: localService.getRefreshToken(),
                });

                window.dispatchEvent(new CustomEvent("profileUpdated"));
              }
            }
          } catch (refreshErr) {
            logger.error("Refresh failed:", refreshErr);
          }
        }

        let storedRedirect = null;
        try {
          const rawFlag = localStorage.getItem(INVOICE_PAYMENT_REDIRECT_KEY);
          if (rawFlag) {
            storedRedirect = JSON.parse(rawFlag);
          }
        } catch (_) {
          storedRedirect = { redirectTo: "/signin" };
        } finally {
          try {
            localStorage.removeItem(INVOICE_PAYMENT_REDIRECT_KEY);
          } catch (_) {}
        }

        const _user = localService.getUser();
        const redirectTo =
          storedRedirect?.redirectTo || (_user ? FeatureAccess.getPostPaymentRedirect(_user) : "/signin");

        // Check if mobile device and redirect to app
        if (isMobileDevice()) {
          try {
            const deepLinkUrl = buildPaymentRedirectDeepLink({
              reference,
              status: status || "success",
              redirectUrl: redirectTo,
            });
            openDeepLink(deepLinkUrl, {
              onFallback: () => {
                const qs = new URLSearchParams({
                  payment: status || "success",
                  reference,
                }).toString();
                history.replace(`${redirectTo}?${qs}`);
              },
            });
          } catch (deepLinkError) {
            const qs = new URLSearchParams({
              payment: status || "success",
              reference,
            }).toString();
            history.replace(`${redirectTo}?${qs}`);
          }
        } else {
          // Desktop: use web redirect with small delay for animation
          const qs = new URLSearchParams({
            payment: status || "success",
            reference,
          }).toString();

          timerRef.current = setTimeout(() => {
            history.replace(`${redirectTo}?${qs}`);
          }, 1400);
        }
      } catch (e) {
        logger.error("Payment redirect error:", e);
        history.replace("/pricing");
      }
    };

    processPaymentRedirect();
  }, [history, location.search, checkOrganizationSubscriptions, loadSubscriptionFeatureFlags]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return (
    <div className="min-h-[70vh] flex items-center justify-center">
      <div className="text-center">
        <div className="mx-auto mb-6 w-56 h-56">
          {animationData ? (
            <Lottie animationData={animationData} loop={false} />
          ) : (
            <div className="mx-auto w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
          )}
        </div>
        <h2 className="mb-1 text-2xl font-semibold text-gray-900">Payment Successful</h2>
        <p className="text-gray-600">Redirecting you to your home screen</p>
      </div>
    </div>
  );
}
